import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { Heart, Ruler, Star } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trackProductView } from "@/components/ProductCard";

export default function ProductDetail() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [size, setSize] = useState("");
    const [qty, setQty] = useState(1);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewData, setReviewData] = useState({ rating: 5, title: "", comment: "" });
    const { addToCart, addWishlist, removeWishlist, isWishlisted } = useCart();
    const { user } = useAuth();
    const nav = useNavigate();

    const load = () => api.get(`/products/${id}`).then(r => { setProduct(r.data); if (r.data?.id) trackProductView(r.data.id); }).catch(() => setProduct("notfound"));
    useEffect(() => { load(); }, [id]);

    if (product === "notfound") return <div className="p-20 text-center font-mono">Product not found.</div>;
    if (!product) return <div className="p-20 text-center font-mono">Loading…</div>;

    const inStock = product.sizes.find(s => s.size === size)?.stock > 0;
    const onSale = product.on_sale;
    const displayPrice = onSale ? product.sale_price : product.price;
    const displayOriginal = onSale ? product.original_price : (product.compare_at_price && product.compare_at_price > product.price ? product.compare_at_price : null);
    const hasDiscount = displayOriginal && displayOriginal > displayPrice;

    const handleAdd = async () => {
        if (!user) { nav(`/login?next=/product/${id}`); return; }
        if (!size) { toast.error("Pick a size"); return; }
        if (!inStock) { toast.error("Out of stock in this size"); return; }
        try { await addToCart(product.id, size, qty); toast.success("Added to cart"); }
        catch { toast.error("Could not add"); }
    };

    const submitReview = async () => {
        if (!user) { nav(`/login?next=/product/${id}`); return; }
        try {
            await api.post(`/products/${product.id}/reviews`, reviewData);
            toast.success("Review posted");
            setReviewOpen(false);
            setReviewData({ rating: 5, title: "", comment: "" });
            load();
        } catch { toast.error("Could not post review"); }
    };

    return (
        <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-10" data-testid="product-detail">
            <div className="grid md:grid-cols-2 gap-10">
                {/* IMAGES */}
                <div className="flex flex-col gap-4">
                    {product.images.map((img, i) => (
                        <div key={i} className="border-2 border-black bg-white overflow-hidden">
                            <Zoom>
                                <img src={img} alt={`${product.name} ${i}`} className="w-full h-auto object-cover" />
                            </Zoom>
                        </div>
                    ))}
                    {product.images.length === 0 && <div className="aspect-square bg-neutral-100 border-2 border-black flex items-center justify-center font-mono text-xs">No image</div>}
                </div>

                {/* INFO */}
                <div className="md:sticky md:top-24 h-fit">
                    <div className="font-mono text-[10px] uppercase text-neutral-500">{product.category_slug} • {product.gender}</div>
                    <h1 className="font-display text-4xl md:text-6xl tracking-tighter mt-1">{product.name}</h1>

                    <div className="flex items-center gap-3 mt-4">
                        <div className="flex items-center gap-1">
                            {[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= Math.round(product.avg_rating) ? "fill-black text-black" : "text-neutral-300"}`} />)}
                        </div>
                        <div className="font-mono text-xs text-neutral-500">{product.review_count} reviews</div>
                    </div>

                    <div className="flex items-center gap-3 mt-6">
                        <div className="font-display text-4xl" style={onSale ? { color: "hsl(var(--accent))" } : {}}>₹{Math.round(displayPrice)}</div>
                        {hasDiscount && <div className="font-mono line-through text-neutral-500">₹{Math.round(displayOriginal)}</div>}
                        {onSale && <div className="font-mono text-[10px] uppercase px-2 py-1 text-white" style={{ background: "hsl(var(--accent))" }}>SALE · {product.sale_name}</div>}
                    </div>

                    <p className="mt-6 text-neutral-700 leading-relaxed">{product.description}</p>

                    {/* SIZE */}
                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-mono text-[10px] uppercase text-neutral-500">Size</div>
                            {product.size_chart && (
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <button className="font-mono text-[10px] uppercase inline-flex items-center gap-1 underline" data-testid="size-chart-btn"><Ruler className="w-3 h-3" /> Size Chart</button>
                                    </DialogTrigger>
                                    <DialogContent className="border-2 border-black">
                                        <DialogHeader><DialogTitle className="font-display text-2xl">Size Chart</DialogTitle></DialogHeader>
                                        <pre className="font-mono text-xs whitespace-pre-wrap">{product.size_chart}</pre>
                                    </DialogContent>
                                </Dialog>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {product.sizes.map(s => {
                                const oos = s.stock <= 0;
                                return (
                                    <button key={s.size} disabled={oos} onClick={() => setSize(s.size)} data-testid={`size-${s.size}`}
                                        className={`px-4 py-2 border-2 border-black font-mono text-xs ${size === s.size ? "bg-black text-white" : "bg-white"} ${oos ? "opacity-40 line-through cursor-not-allowed" : ""}`}>
                                        {s.size}
                                    </button>
                                );
                            })}
                        </div>
                        {size && (
                            <div className="mt-2 font-mono text-[10px] uppercase" data-testid="stock-status">
                                {inStock ? <span className="text-green-700">In Stock</span> : <span className="text-[hsl(var(--accent))]">Out of Stock</span>}
                            </div>
                        )}
                    </div>

                    {/* QUANTITY */}
                    <div className="mt-6 flex items-center gap-4">
                        <div className="font-mono text-[10px] uppercase text-neutral-500">Qty</div>
                        <div className="inline-flex border-2 border-black">
                            <button onClick={() => setQty(Math.max(1, qty-1))} className="px-3 py-1 border-r-2 border-black" data-testid="qty-minus">−</button>
                            <div className="px-4 py-1 font-mono min-w-[40px] text-center">{qty}</div>
                            <button onClick={() => setQty(qty+1)} className="px-3 py-1 border-l-2 border-black" data-testid="qty-plus">+</button>
                        </div>
                    </div>

                    {/* ACTIONS */}
                    <div className="mt-8 flex gap-3">
                        <button onClick={handleAdd} data-testid="add-to-cart-btn" className="flex-1 bg-black text-white px-6 py-4 border-2 border-black font-mono uppercase text-xs tracking-widest brutal-shadow">Add to Cart</button>
                        <button
                            onClick={() => {
                                if (!user) { nav(`/login?next=/product/${id}`); return; }
                                isWishlisted(product.id) ? removeWishlist(product.id) : addWishlist(product.id);
                            }}
                            data-testid="wishlist-btn"
                            className="px-4 py-4 border-2 border-black bg-white">
                            <Heart className={`w-5 h-5 ${isWishlisted(product.id) ? "fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" : ""}`} />
                        </button>
                    </div>
                </div>
            </div>

            {/* REVIEWS */}
            <div className="mt-24 border-t-2 border-black pt-10">
                <div className="flex items-end justify-between mb-8">
                    <h2 className="font-display text-4xl md:text-5xl tracking-tighter">Reviews</h2>
                    <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                        <DialogTrigger asChild>
                            <button className="font-mono text-xs uppercase px-4 py-2 border-2 border-black brutal-shadow" data-testid="write-review-btn">Write a Review</button>
                        </DialogTrigger>
                        <DialogContent className="border-2 border-black">
                            <DialogHeader><DialogTitle className="font-display text-2xl">Rate this product</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div className="flex gap-1">
                                    {[1,2,3,4,5].map(s => (
                                        <button key={s} onClick={() => setReviewData({...reviewData, rating: s})} data-testid={`review-star-${s}`}>
                                            <Star className={`w-8 h-8 ${s <= reviewData.rating ? "fill-black text-black" : "text-neutral-300"}`} />
                                        </button>
                                    ))}
                                </div>
                                <input placeholder="Title" value={reviewData.title} onChange={e => setReviewData({...reviewData, title: e.target.value})} className="w-full border-2 border-black p-3 font-mono text-sm" data-testid="review-title" />
                                <textarea placeholder="Your thoughts..." rows={5} value={reviewData.comment} onChange={e => setReviewData({...reviewData, comment: e.target.value})} className="w-full border-2 border-black p-3 font-mono text-sm" data-testid="review-comment" />
                                <button onClick={submitReview} className="w-full bg-black text-white py-3 font-mono uppercase text-xs tracking-widest" data-testid="submit-review-btn">Post Review</button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {product.reviews.length === 0 ? (
                    <div className="border-2 border-black p-10 text-center font-mono text-sm">No reviews yet — be the first.</div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                        {product.reviews.map(r => (
                            <div key={r.id} className="border-2 border-black p-6" data-testid={`review-${r.id}`}>
                                <div className="flex items-center gap-2">
                                    {[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= r.rating ? "fill-black text-black" : "text-neutral-300"}`} />)}
                                </div>
                                <div className="mt-2 font-display text-xl">{r.title || "Nice"}</div>
                                <p className="mt-2 text-sm text-neutral-700">{r.comment}</p>
                                <div className="mt-3 font-mono text-[10px] uppercase text-neutral-500">{r.user_name} • {new Date(r.created_at).toLocaleDateString()}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
